import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import type { MenuDataItem } from '@ant-design/pro-components';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

import { ToastContainer } from 'react-toastify';
import { API } from './helpers/api';
import { listMyHologramDevices } from './services/hologramCoverApi';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

// 获取控制台可见配置（需登录）。未登录时绝不能请求，否则会 401 → axios 里 showError 强制跳 /login，形成「一刷新就回登录」
async function fetchVisibleConfig() {
  if (typeof localStorage === 'undefined' || !localStorage.getItem('user')) {
    return undefined;
  }
  try {
    const res = await API.get('/api/user/config/visible');
    if (res.data?.data) return res.data.data;
    return res.data;
  } catch (e) {
    return undefined;
  }
}

// 名下有全息仓（quanxicang-s3）时显示「全息仓画面」菜单
async function fetchHasHologramDevice() {
  if (typeof localStorage === 'undefined' || !localStorage.getItem('user')) {
    return false;
  }
  try {
    return (await listMyHologramDevices()).length > 0;
  } catch (e) {
    return false;
  }
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
  branding?: Record<string, any>;
  visibleConfig?: any;
  hasHologramDevice?: boolean;
}> {
  const fetchUserInfo = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        return undefined;
      }
      const parsedUser: any = JSON.parse(userStr);
      // /api/user/profile 无条件下发 supplierCode/tenantCode（空串=已解绑），是企业字段的权威来源。
      // 后台解绑企业后 profile 返回空串，必须覆盖 localStorage 登录快照的旧值，
      // 否则解绑后前端永远拿旧编码，access.ts 会把平台 admin 误判为企业账号。
      let profile: Record<string, any> | undefined;
      try {
        const res = await API.get('/api/user/profile', {
          skipGlobalErrorHandler: true,
        } as Parameters<typeof API.get>[1]);
        if (res.data?.status === 'ok' && res.data?.data) {
          profile = res.data.data as Record<string, any>;
          parsedUser.tenantCode = String(profile.tenantCode ?? '').trim();
          parsedUser.supplierCode = String(profile.supplierCode ?? '').trim();
          if (profile.role !== undefined && profile.role !== null && profile.role !== '') {
            parsedUser.role = profile.role;
          }
          // 同步开放平台/开发者状态，避免登录快照里的旧值（false）导致菜单不显示
          if (profile.openapiDeveloperEnabled !== undefined) {
            parsedUser.openapiDeveloperEnabled = !!profile.openapiDeveloperEnabled;
          }
          if (profile.openapiLevel !== undefined) {
            parsedUser.openapiLevel = profile.openapiLevel;
          }
          if (profile.githubLogin !== undefined) {
            parsedUser.githubLogin = profile.githubLogin;
          }
          if (profile.githubVerified !== undefined) {
            parsedUser.githubVerified = !!profile.githubVerified;
          }
          if (profile.githubVerifiedAt !== undefined) {
            parsedUser.githubVerifiedAt = profile.githubVerifiedAt;
          }
          if (profile.enterpriseCertified !== undefined) {
            parsedUser.enterpriseCertified = !!profile.enterpriseCertified;
          }
          if (profile.hasCommercialDevice !== undefined) {
            parsedUser.hasCommercialDevice = !!profile.hasCommercialDevice;
          }
          localStorage.setItem('user', JSON.stringify(parsedUser));
        }
      } catch {
        // 未带 token 或已失效时仍使用 localStorage 快照
      }
      const role = profile?.role || parsedUser.role;
      // profile 成功返回时以 profile 为准（空串=已解绑）；仅 profile 拉取失败时回退登录快照
      const tenantCode = profile
        ? String(profile.tenantCode ?? '').trim()
        : String(parsedUser.tenantCode ?? '').trim();
      const supplierCode = profile
        ? String(profile.supplierCode ?? '').trim()
        : String(parsedUser.supplierCode ?? '').trim();
      // 开放平台/开发者状态：profile 成功返回时以 profile 为准，失败时回退登录快照
      const openapiDeveloperEnabled = profile
        ? !!profile.openapiDeveloperEnabled
        : !!parsedUser.openapiDeveloperEnabled;
      const openapiLevel = profile ? profile.openapiLevel : parsedUser.openapiLevel;
      const githubVerified = profile
        ? !!profile.githubVerified || !!profile.githubId || !!profile.githubVerifiedAt
        : !!parsedUser.githubVerified;
      const githubLogin = profile?.githubLogin || parsedUser.githubLogin || '';
      const enterpriseCertified = profile
        ? !!profile.enterpriseCertified || !!profile.supplierCode
        : !!parsedUser.enterpriseCertified || !!supplierCode;
      const hasCommercialDevice = profile
        ? !!profile.hasCommercialDevice
        : !!parsedUser.hasCommercialDevice;
      return {
        name: parsedUser.username,
        username: parsedUser.username, // 显式设置 username 字段
        nickname:
          (profile?.nickname as string | undefined) ||
          parsedUser.nickname ||
          parsedUser.username,
        avatar:
          'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
        userid: '00000001',
        email: profile?.email ?? parsedUser.email,
        tags: [],
        role,
        access: role, // access 字段同步为 role
        tenantCode, // 用户归属企业编码
        supplierCode, // 企业认证账号编码：非空即为企业（租户）管理账号（以服务端 profile 为准）
        openapiDeveloperEnabled, // 开放平台是否开通（决定「应用与密钥」等菜单是否显示）
        openapiLevel,
        githubVerified,
        githubLogin,
        enterpriseCertified,
        hasCommercialDevice,
        geographic: {},
        address: '',
        phone: '',
      };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // load runtime branding from public/branding.json (fallback to defaults in config/defaultSettings)
  const loadBranding = async () => {
    try {
      // detect locale from common app locations, then navigator, fallback to zh-CN
      const rawLocale =
        (window as any).__umi_locale ||
        localStorage.getItem('umi_locale') ||
        navigator.language ||
        'zh-CN';
      const locale = String(rawLocale || 'zh-CN').replace('_', '-');
      const parts = locale.split('-');
      const full = locale;
      const lang = parts[0];

      const candidates: string[] = [];
      candidates.push(`/branding.${full}.json`);
      if (lang && lang !== full) candidates.push(`/branding.${lang}.json`);
      candidates.push('/branding.json');

      for (const url of candidates) {
        try {
          const res = await fetch(url, { cache: 'no-cache' });
          if (!res.ok) continue;
          const json = await res.json();
          return json;
        } catch (e) {
          // ignore error, try next candidate
        }
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  };

  // 如果不是公共页面，执行
  const { location } = history;
  const publicPaths = ['/login', '/register', '/forgot-password', '/cancel-account', '/mcp-tutorial'];
  if (!publicPaths.includes(location.pathname)) {
    const [currentUser, branding, visibleConfig, hasHologramDevice] = await Promise.all([
      fetchUserInfo(),
      loadBranding(),
      fetchVisibleConfig(),
      fetchHasHologramDevice(),
    ]);
    return {
      fetchUserInfo,
      currentUser,
      branding,
      visibleConfig,
      hasHologramDevice,
      settings: { ...(defaultSettings as Partial<LayoutSettings>) },
    };
  }

  const branding = await loadBranding();
  return {
    fetchUserInfo,
    branding,
    visibleConfig: undefined,
    settings: { ...(defaultSettings as Partial<LayoutSettings>) },
  };
}

// 递归处理菜单数据，动态设置 speakers、recorderManage、voiceClone、4G 充值 的 hideInMenu
function patchMenuData(
  items: MenuDataItem[],
  speakerDetect: boolean,
  recorderManagerEnable: boolean,
  voiceCloneEnable: boolean,
  simRechargeWebEnable: boolean,
  hasHologramDevice: boolean,
): MenuDataItem[] {
  return items.map((item) => {
    let newItem = { ...item };
    if (item.path === '/console/speakers') {
      newItem.hideInMenu = !speakerDetect;
    }
    if (item.path === '/console/hologram') {
      newItem.hideInMenu = !hasHologramDevice;
    }
    if (item.path === '/admin/recorder') {
      newItem.hideInMenu = !recorderManagerEnable;
    }
    if (item.path === '/console/voiceClone') {
      newItem.hideInMenu = !voiceCloneEnable;
    }
    if (item.path === '/console/sim-recharge') {
      newItem.hideInMenu = !simRechargeWebEnable;
    }
    if (item.routes) {
      (newItem as any).routes = patchMenuData(
        item.routes,
        speakerDetect,
        recorderManagerEnable,
        voiceCloneEnable,
        simRechargeWebEnable,
        hasHologramDevice,
      );
    }
    if (item.children) {
      newItem.children = patchMenuData(
        item.children,
        speakerDetect,
        recorderManagerEnable,
        voiceCloneEnable,
        simRechargeWebEnable,
        hasHologramDevice,
      );
    }
    return newItem;
  });
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 动态获取声纹识别开关和录音管理开关，兼容多种字段和类型
  const appConfig = initialState?.visibleConfig;
  const speakerDetect =
    appConfig?.SpeakerDetect === true ||
    appConfig?.SpeakerDetect === 'true';
  const recorderManagerEnable =
    appConfig?.RecorderManagerEnable === true ||
    appConfig?.RecorderManagerEnable === 'true';
  const voiceCloneEnable =
    appConfig?.VoiceCloneEnable === true ||
    appConfig?.VoiceCloneEnable === 'true';
  const simRechargeWebEnable =
    appConfig?.SimRechargeWebEnable === true ||
    appConfig?.SimRechargeWebEnable === 'true';
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      // content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 未登录时回到登录页。除 initialState 外再读 localStorage，避免登录后立刻跳转时 state 尚未带上 currentUser 被误判
      const hasSession =
        !!initialState?.currentUser ||
        (typeof localStorage !== 'undefined' && !!localStorage.getItem('user'));
      const publicPaths = ['/login', '/register', '/forgot-password', '/cancel-account', '/mcp-tutorial'];
      if (!hasSession && !publicPaths.includes(location.pathname)) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev ? [] : [],
  menuHeaderRender: undefined,
  menuDataRender: (menuData) =>
      patchMenuData(
        menuData,
        speakerDetect,
        recorderManagerEnable,
        voiceCloneEnable,
        simRechargeWebEnable,
        !!initialState?.hasHologramDevice,
      ),
    childrenRender: (children: React.ReactNode) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    // Prefer branding values from runtime branding (public/branding.json) when available.
    title: initialState?.branding?.title || initialState?.settings?.title,
    // @ts-ignore - logo may not exist on ProSettings type; provide at runtime
    logo: initialState?.branding?.logoUrl || (initialState?.settings as any)?.logo,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
